import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { CsvInfo } from '../../../../states/types/export'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { format } from '../../../../utils/date'
import { useTranslation } from 'react-i18next'
import ExportModal from '../../../components/export/csv/ExportModal'
import { fetchCsvFile, fetchCsvExportList } from '../../../../states/containers/export/exportSlice'
import Loading from '../../../components/common/Loading'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactGA from 'react-ga4'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { fetchTeamList } from '../../../../states/containers/team/listSlice'

interface ExportModalState {
  display: boolean,
  type: string,
  id: number | undefined,
}

const Index: FC = () => {
  const { language } = useParams<{ language: LangValue }>()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    commonPart,
    teamList,
  } = useSelector((state: RootState) => (state))

  const { csvList, isLoading } = useSelector((state: RootState) => (state.export))

  const [exportModalState, setExportModalState] = useState<ExportModalState>({
    display: false,
    type: "",
    id: undefined,
  })

  const [isListLoading, setIsListLoading] = useState<boolean>(true)

  useEffect(() => {
    if (!commonPart.company?.id) return
    dispatch(fetchCsvExportList())
    if (!teamList.teams.length) {
      dispatch(
        fetchTeamList(() => { /* do nothing */ }),
      )
    }
  }, [commonPart])

  useEffect(() => {
    setIsListLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    setIsListLoading(true)
  }, [])

  const openExportModal = (id: number, type: string) => {
    setExportModalState({
      ...exportModalState,
      display: true,
      type: type,
      id: id,
    })
  }

  const closeExportModal = () => {
    setExportModalState({
      ...exportModalState,
      display: false,
    })
  }

  const reloadList = () => {
    dispatch(fetchCsvExportList())
  }

  const downloadFile = (id: number, type: string) => {
    dispatch(
      fetchCsvFile(
        id,
        (url) => {
          window.location.href = url
          ReactGA.event({
            category: 'CsvExport',
            action: 'Download',
            label: type,
            value: 1,
          })
        },
      ),
    )
  }

  const displayList = () => (
    csvList && csvList.length ? (
      csvList.map((info: CsvInfo) => (
        <tr key={info.id}>
          <td>
            {format(info.target_date, t('time.date.formats.export_target_date'))}
          </td>
          <td>
            {t(`export.select_type.${info.file_type}`)}
          </td>
          <td>
            {
              info.team_ids?.split(',').length ? (
                <>
                  {
                    info.team_ids.split(',').map((v) => (
                      <div className="element_block">
                        {teamList.teams.find((w) => w.team_id === Number(v))?.name}
                      </div>
                    ))
                  }
                </>
              ) : (
                <>
                  全てのメンバー
                </>
              )
            }
          </td>
          <td>{info.code || "-"}</td>
          <td>
            {
              info.url_path && (info.status === "no_send" || info.status === "complete") ? (
                <button type="button" className="flex download" onClick={() => downloadFile(info.id, t(`export.select_type.${info.file_type}`))}>
                  <FontAwesomeIcon
                    icon={['fas', 'file-csv']}
                    className="csv_icon"
                  />
                  {t('export.button.download')}
                </button>
              ) : (
                <div className="generating">
                  {t('export.list.generating')}
                </div>
              )
            }
          </td>
          <td>
            {
              info.url_path ? (
                format(info.expire_date, t('time.date.formats.export_expire_date'))
              ) : (
                "-"
              )
            }
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td className="no_data">
          <div className="text">
            {t('export.list.no_data')}
          </div>
          <button
            type="button"
            className="button add_one_member"
            onClick={(event) => openExportModal(Number(event.currentTarget.dataset.id), "create")}
          >
            {t('export.button.create')}
          </button>
        </td>
      </tr>
    )
  )

  return (
    <section className="export_page">
      <Helmet>
        <title>Qasee - 組織分析 - データエクスポート - 計測データ</title>
      </Helmet>
      <section className="breadcrumb">
        <Link
          to={setPath(URL.ANALYZE.path, language)}
        >
          組織分析
        </Link>
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.EXPORT.path, language)}
        >
          データエクスポート
        </Link>
        &nbsp;&gt;&nbsp;
        計測データ
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          計測データ
        </h1>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div className="block-header">
            <div className="description">
              {/* eslint-disable-next-line react/no-danger */}
              <p dangerouslySetInnerHTML={{ __html: t('export.csv.description') }} />
            </div>
            <div
              className="button-area"
              style={{
                display: "flex",
                gap: "16px",
              }}
            >
              <button
                type="button"
                className="reload"
                onClick={reloadList}
              >
                <FontAwesomeIcon icon={['fas', 'redo-alt']} className="icon" />
              </button>
              <button
                type="button"
                className="button add_one_member"
                onClick={(event) => openExportModal(Number(event.currentTarget.dataset.id), "create")}
              >
                {t('export.button.create')}
              </button>
            </div>
          </div>
          <div className="block-detail">
            <table className="flex-table export_csv_table">
              <thead>
                <tr>
                  <th>{t('export.list.target_ym')}</th>
                  <th>{t('export.list.type')}</th>
                  <th>出力対象</th>
                  <th>{t('export.list.character_code')}</th>
                  <th>{t('export.list.status')}</th>
                  <th>{t('export.list.expired_at')}</th>
                </tr>
              </thead>
              <tbody>
                {
                  isListLoading ? (
                    <Loading />
                  ) : (
                    displayList()
                  )
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {/* 編集モーダル */}
      <ExportModal
        exportModalState={exportModalState}
        closeCallback={closeExportModal}
      />
    </section>
  )
}
export default Index
